/**
 * Configuration Factory - Pure Functions for Configuration Creation
 *
 * Provides standalone factory functions for creating configurations without
 * needing to instantiate ProjectOrchestrator. This simplifies the UI layer
 * integration while maintaining the same behavior.
 *
 * @module engine/orchestration/configuration-factory
 */

import { logger } from '~/services/logging';
import type { BondSeries, Configuration, Project } from '~/types';
import { generateConfigurationName } from '~/utils/date-utils';

/**
 * Context for configuration creation (for scenario tracking)
 */
export interface ConfigurationContext {
  scenarioId?: string;
  actionId?: string;
}

/**
 * Creates a new configuration with the provided project and bond series.
 *
 * This is a pure factory function that creates a Configuration object
 * without side effects. It handles:
 * - Generating unique or deterministic IDs
 * - Calculating the next orderId from existing configurations
 * - Copying millage and DRF inputs from project
 * - Setting timestamps
 *
 * @param project - The project context (includes existing configurations, inputs)
 * @param bondSeries - The bond series for this configuration
 * @param context - Optional context for scenario/action tracking
 * @returns A new Configuration object ready for processing
 *
 * @example
 * ```typescript
 * import { createConfiguration } from '~/engine';
 *
 * const newConfig = createConfiguration(project, [bond1, bond2]);
 * ```
 */
export function createConfiguration(
  project: Project,
  bondSeries: BondSeries[],
  context?: ConfigurationContext
): Configuration {
  const currentConfigurations = project.configurations || [];

  // Calculate the next orderId by finding the highest existing orderId
  const highestOrderId = currentConfigurations.reduce(
    (max, config) => Math.max(max, config.orderId || 0),
    0
  );

  // Generate deterministic ID for scenario actions, random for others
  const configurationId =
    context?.scenarioId && context?.actionId
      ? `${context.scenarioId}-${context.actionId}`
      : crypto.randomUUID();

  // Get inputs from project with defaults
  const millageInputs = project.millageInputs || {
    peakMillage: '',
    peakMillageDuration: '',
    minimumMillage: '',
    minimumMillageDuration: '',
  };

  const drfInputs = project.drfInputs || {
    startingBalance: '',
    maximumBalance: '',
    minimumBalance: '',
  };

  const orderId = highestOrderId + 1;
  const newConfiguration: Configuration = {
    id: configurationId,
    orderId,
    createdAt: new Date().toISOString(),
    name: generateConfigurationName(orderId, undefined, currentConfigurations), // Set default name in YYMMDD.HHMM or YYMMDD.HHMM.[order] format
    totalCandidates: 0,
    bondSeries: [...bondSeries], // Copy to avoid mutation
    candidates: [],
    millageInputs,
    drfInputs,
    scenarioId: context?.scenarioId,
    actionId: context?.actionId,
  };

  logger.L1.debug(`Created configuration via factory: ${configurationId}`, {
    configurationId,
    orderId: newConfiguration.orderId,
    bondSeriesCount: bondSeries.length,
    scenarioId: context?.scenarioId,
  });

  return newConfiguration;
}
